import {Template} from "../../../components/template/template.jsx";
import GarrisonTimelineFilters from "./components/garrison-timeline-filters.jsx";
import React, {useEffect, useMemo, useState} from "react";
import {useInfiniteQuery, useQuery} from "@tanstack/react-query";
import {garrisonTimelineErrorMappings, garrisonTimelineFilters, garrisonTimelineKeys, garrisonTimelineModes} from "./utils/garrison-timeline-utils.js";
import useGarrisonTimelineQueries from "./hooks/use-garrison-timeline-queries.js";
import TimelineView from "./components/timeline/timeline-view.jsx";
import garrisonTimelineService from "../../../services/dispatch/garrison-timeline-service.js";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import corporationService from "../../../services/web/corporation-service.js";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import {buildGarrisonTimelinePrintUrl} from "./utils/garrison-timeline-utils.js";
import projectUtils from "../../../utils/project-utils.js";
import translateErrorMessage from "../../../utils/translate-error-message.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import "../../../css/style-report.css";

const GarrisonTimeline = () => {
  const navigate = navigateHook();
  const [filters, setFilters] = useState(garrisonTimelineFilters);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [mode, setMode] = useState(garrisonTimelineModes.timeline);
  const [zoom, setZoom] = useState({start: 0, end: 100});
  const [cropRange, setCropRange] = useState(null);
  const {leaders, teams, contingent, isLoading} = useGarrisonTimelineQueries();
  const {data: timelinePages, isLoading: isLoadingTimeline, isFetchingNextPage, fetchNextPage, hasNextPage, isError: isTimelineError, error: timelineError} = useInfiniteQuery({
    queryKey: garrisonTimelineKeys.timeline(appliedFilters),
    queryFn: ({pageParam = 0}) => garrisonTimelineService.apply2({...appliedFilters, page: pageParam}),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;

      return nextPage * lastPage.pageSize < lastPage.totalGarrisons ? nextPage : undefined;
    },
    enabled: !!appliedFilters,
    retry: 0,
  });
  const timelineData = useMemo(() => {
    const pages = timelinePages?.pages;
    if (!pages?.length) return null;

    const [firstPage] = pages;
    const dispatches = new Map();

    pages.forEach(currentPage => {
      currentPage.dispatches?.forEach(dispatch => dispatches.set(dispatch.dispatchId, dispatch));
    });

    return {
      ...firstPage,
      garrisons: pages.flatMap(currentPage => currentPage.garrisons ?? []),
      dispatches: Array.from(dispatches.values()),
      timeline: Object.assign({}, ...pages.map(currentPage => currentPage.timeline ?? {})),
      ganttSegments: Object.assign({}, ...pages.map(currentPage => currentPage.ganttSegments ?? {})),
      page: 0,
      totalGarrisons: firstPage.totalGarrisons
    };
  }, [timelinePages]);

  useEffect(() => {
    if (!isTimelineError) return;

    projectUtils.swalErrorMessage(
      "",
      translateErrorMessage(
        timelineError,
        "Não foi possível pesquisar a linha do tempo.",
        garrisonTimelineErrorMappings
      ),
      "Ok"
    );
  }, [isTimelineError, timelineError]);

  const {data: corporation = null, isLoading: isCorporationLoading} = useQuery({
    queryFn: corporationService.entity,
    queryKey: garrisonTimelineKeys.corporation()
  })

  const handleAppliedFilters = (nextFilters) => {
    setElapsedSeconds(0);
    setZoom({start: 0, end: 100});
    setCropRange(null);
    setAppliedFilters(nextFilters);
  };

  const openPrintPreview = () => navigate(buildGarrisonTimelinePrintUrl({filters: appliedFilters, zoom, elapsedSeconds, cropRange}), true);

  return(
    <Template isLoading={isLoading || isLoadingTimeline || isCorporationLoading}>
      <Template.Header>
        <div className="flex justify-between w-full">
          <Template.Title>Linha do tempo da guarnição</Template.Title>
          <HeaderButtons
            printable={true}
            printComponentRef={{current: {print: openPrintPreview}}}
            reportData={timelineData?.garrisons}
            data={timelineData?.garrisons?.length}
          />
        </div>
      </Template.Header>
      <Template.Body className="space-y-2">
        <GarrisonTimelineFilters filters={filters} setFilters={setFilters} setAppliedFilters={handleAppliedFilters} leaders={leaders} teams={teams} contingent={contingent}/>

        <div className="h-[calc(100vh-133px)]">
          {timelineData && timelineData?.garrisons?.length > 0 && (
            <>
              <div className="h-full w-full">
                <TimelineView data={timelineData}
                              filter={appliedFilters}
                              mode={mode}
                              setMode={setMode}
                              corporation={corporation}
                              zoom={zoom}
                              setZoom={setZoom}
                              elapsedSeconds={elapsedSeconds}
                              setElapsedSeconds={setElapsedSeconds}
                              hasNextPage={hasNextPage}
                              isFetchingNextPage={isFetchingNextPage}
                              onLoadMore={fetchNextPage}
                              onVisualStateChange={({cropRange: nextCropRange}) => setCropRange(nextCropRange)}
                />
              </div>
            </>
          )}
          <NotAppliedContent reportData={timelineData} customMessage="Aplique os filtros para exibir o resultado."/>
          <NoDataContent reportData={timelineData} data={timelineData?.garrisons?.length}/>
        </div>
      </Template.Body>
    </Template>
  )
}

export default GarrisonTimeline;
